import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, query, onSnapshot, doc, addDoc, 
    updateDoc, deleteDoc, orderBy, setDoc, getDoc, serverTimestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../App';
import { appId } from '../config/firebaseConfig';

export default function AvisosPage({ permissionLevel }) {
    const isReadOnly = permissionLevel === 'read';
    const isUserAdmin = useMemo(() => auth.currentUser?.email === 'jonathan@arcusinformatica.com.br', []);

    // Estados de dados
    const [avisos, setAvisos] = useState([]);
    const [clientsContrato, setClientsContrato] = useState([]);
    const [clientsAvulsos, setClientsAvulsos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estados do Formulário de Cadastro
    const [formData, setFormData] = useState({
        assunto: '',
        descricao: '',
        clienteId: '',
        clienteNome: '',
        clienteEmail: '',
        dataAviso: '',
        recorrencia: 'unico'
    });

    // Estados do Painel de Configuração de E-mail
    const [smtpConfig, setSmtpConfig] = useState({
        provider: 'smtp',
        host: '',
        port: '465',
        user: '',
        pass: '',
        from: '',
        resendApiKey: '',
        resendFrom: 'onboarding@resend.dev'
    });

    const [isSmtpEditing, setIsSmtpEditing] = useState(false);
    const [testRecipientEmail, setTestRecipientEmail] = useState('');
    const [statusMsg, setStatusMsg] = useState({ type: '', msg: '' });
    const [smtpStatusMsg, setSmtpStatusMsg] = useState({ type: '', msg: '' });
    const [loadingAction, setLoadingAction] = useState(null); // 'save-aviso', 'save-smtp', 'test-smtp', 'test-aviso-id'
    const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, type: '', title: '', message: '' });
    
    // Novos estados de modal e filtros
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isSmtpModalOpen, setIsSmtpModalOpen] = useState(false);
    const [filterClienteId, setFilterClienteId] = useState('all');
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
    const [logs, setLogs] = useState([]);

    // Coleção unificada de clientes
    const allClients = useMemo(() => {
        const contrato = clientsContrato.map(c => ({ ...c, tipo: 'contrato' }));
        const avulso = clientsAvulsos.map(c => ({ ...c, tipo: 'avulso' }));
        return [...contrato, ...avulso].filter(c => c.ativo !== false).sort((a, b) => a.name.localeCompare(b.name));
    }, [clientsContrato, clientsAvulsos]);

    useEffect(() => {
        // Escutar Avisos
        const qAvisos = query(
            collection(db, `/artifacts/${appId}/public/data/avisos`),
            orderBy('dataAviso', 'asc')
        );
        const unsubAvisos = onSnapshot(qAvisos, (snap) => {
            setAvisos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        // Escutar Clientes Contrato
        const unsubContrato = onSnapshot(
            collection(db, `/artifacts/${appId}/public/data/clients`),
            (snap) => setClientsContrato(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })))
        );

        // Escutar Clientes Avulsos
        const unsubAvulsos = onSnapshot(
            collection(db, `/artifacts/${appId}/public/data/clients-avulsos`),
            (snap) => setClientsAvulsos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })))
        );

        // Escutar Logs
        const qLogs = query(
            collection(db, `/artifacts/${appId}/public/data/avisos_logs`),
            orderBy('timestamp', 'desc')
        );
        const unsubLogs = onSnapshot(qLogs, (snap) => {
            setLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Buscar Configuração SMTP existente
        const fetchSmtp = async () => {
            try {
                const docRef = doc(db, `/artifacts/${appId}/public/data/settings/email_config`);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    setSmtpConfig(snap.data());
                }
            } catch (err) {
                console.error("Erro ao carregar SMTP:", err);
            }
        };
        fetchSmtp();

        if (auth.currentUser?.email) {
            setTestRecipientEmail(auth.currentUser.email);
        }

        return () => {
            unsubAvisos();
            unsubContrato();
            unsubAvulsos();
            unsubLogs();
        };
    }, []);

    // Calcula diferença em dias a partir da data de aviso YYYY-MM-DD
    const getDaysRemaining = (dateStr) => {
        if (!dateStr) return null;
        const parts = dateStr.split('-');
        const dateAviso = new Date(parts[0], parts[1] - 1, parts[2]);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const diffTime = dateAviso.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Submete cadastro do alerta
    const handleSaveAviso = async (e) => {
        e.preventDefault();
        if (isReadOnly) return;
        if (!formData.assunto || !formData.clienteId || !formData.dataAviso) {
            setStatusMsg({ type: 'error', msg: 'Por favor, preencha todos os campos obrigatórios.' });
            return;
        }

        setLoadingAction('save-aviso');
        setStatusMsg({ type: '', msg: '' });

        try {
            const selectedClient = allClients.find(c => c.id === formData.clienteId);
            const avisoPayload = {
                assunto: formData.assunto,
                descricao: formData.descricao,
                clienteId: formData.clienteId,
                clienteNome: selectedClient ? (selectedClient.nomeFantasia || selectedClient.name) : '',
                clienteEmail: selectedClient?.email || formData.clienteEmail || '',
                dataAviso: formData.dataAviso,
                recorrencia: formData.recorrencia || 'unico',
                createdAt: serverTimestamp(),
                createdBy: auth.currentUser?.email || 'admin',
                emailsDisparados: {
                    '7days': false
                }
            };

            if (formData.id) {
                // Editar existente
                const { id, ...updateData } = formData;
                await updateDoc(doc(db, `/artifacts/${appId}/public/data/avisos/${id}`), updateData);
                setStatusMsg({ type: 'success', msg: 'Aviso atualizado com sucesso!' });

                // Log de edição
                await addDoc(collection(db, `/artifacts/${appId}/public/data/avisos_logs`), {
                    action: 'editar',
                    avisoId: id,
                    avisoAssunto: updateData.assunto,
                    clienteNome: updateData.clienteNome,
                    user: auth.currentUser?.email || 'admin',
                    timestamp: serverTimestamp(),
                    detalhes: `Editou o alerta. Nova data limite: ${updateData.dataAviso}, Recorrência: ${updateData.recorrencia}`
                });
            } else {
                // Criar novo
                const docRef = await addDoc(collection(db, `/artifacts/${appId}/public/data/avisos`), avisoPayload);
                setStatusMsg({ type: 'success', msg: 'Aviso cadastrado com sucesso!' });

                // Log de criação
                await addDoc(collection(db, `/artifacts/${appId}/public/data/avisos_logs`), {
                    action: 'criar',
                    avisoId: docRef.id,
                    avisoAssunto: avisoPayload.assunto,
                    clienteNome: avisoPayload.clienteNome,
                    user: auth.currentUser?.email || 'admin',
                    timestamp: serverTimestamp(),
                    detalhes: `Cadastrou o alerta. Data limite: ${avisoPayload.dataAviso}, Recorrência: ${avisoPayload.recorrencia}`
                });
            }

            // Resetar formulário
            setFormData({ assunto: '', descricao: '', clienteId: '', clienteNome: '', clienteEmail: '', dataAviso: '', recorrencia: 'unico' });
            setIsFormModalOpen(false);
        } catch (error) {
            console.error("Erro ao salvar aviso:", error);
            setStatusMsg({ type: 'error', msg: 'Falha ao salvar o aviso no banco de dados.' });
        } finally {
            setLoadingAction(null);
        }
    };

    // Apaga aviso
    const handleDeleteAviso = (id) => {
        if (isReadOnly) return;
        const avisoToDelete = avisos.find(a => a.id === id);
        setConfirmModal({
            isOpen: true,
            title: 'Excluir Alerta',
            message: 'Deseja realmente remover este aviso? Esta ação não poderá ser desfeita.',
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, `/artifacts/${appId}/public/data/avisos/${id}`));
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));

                    // Log de exclusão
                    if (avisoToDelete) {
                        await addDoc(collection(db, `/artifacts/${appId}/public/data/avisos_logs`), {
                            action: 'excluir',
                            avisoId: id,
                            avisoAssunto: avisoToDelete.assunto,
                            clienteNome: avisoToDelete.clienteNome,
                            user: auth.currentUser?.email || 'admin',
                            timestamp: serverTimestamp(),
                            detalhes: `Excluiu o alerta`
                        });
                    }
                } catch (err) {
                    console.error("Erro ao deletar:", err);
                }
            }
        });
    };

    // Prepara formulário para editar
    const handleEditAviso = (aviso) => {
        setFormData(aviso);
        setIsFormModalOpen(true);
    };

    // Salva configuração de SMTP
    const handleSaveSmtp = async (e) => {
        e.preventDefault();
        if (!isUserAdmin) return;
        setLoadingAction('save-smtp');
        setSmtpStatusMsg({ type: '', msg: '' });

        try {
            await setDoc(doc(db, `/artifacts/${appId}/public/data/settings/email_config`), {
                ...smtpConfig,
                updatedAt: serverTimestamp()
            });
            setSmtpStatusMsg({ type: 'success', msg: 'Configuração SMTP salva com sucesso!' });
            setIsSmtpEditing(false);
            setIsSmtpModalOpen(false);
        } catch (error) {
            console.error("Erro ao salvar SMTP:", error);
            setSmtpStatusMsg({ type: 'error', msg: 'Falha ao salvar configurações de SMTP.' });
        } finally {
            setLoadingAction(null);
        }
    };

    // Dispara teste manual do SMTP geral ou de um aviso específico
    const handleDispararTesteManual = async (avisoId = null) => {
        const actionKey = avisoId ? `test-aviso-${avisoId}` : 'test-smtp';
        setLoadingAction(actionKey);
        
        try {
            const functions = getFunctions();
            const triggerTest = httpsCallable(functions, 'dispararAvisoTesteManual');
            
            const payload = {
                avisoId: avisoId,
                emailDestino: avisoId ? null : (testRecipientEmail || auth.currentUser?.email)
            };

            // Se for teste do painel SMTP sem ter salvo, envia a config provisória
            if (!avisoId) {
                payload.configTeste = smtpConfig;
            }

            const response = await triggerTest(payload);
            
            if (response.data?.success) {
                const dest = response.data.destinatario;
                setFeedbackModal({
                    isOpen: true,
                    type: 'success',
                    title: 'E-mail Enviado!',
                    message: `Aviso enviado com sucesso para:\n${dest}`
                });
            } else {
                throw new Error("Resposta de sucesso não confirmada.");
            }
        } catch (error) {
            console.error("Erro no teste SMTP:", error);
            setFeedbackModal({
                isOpen: true,
                type: 'error',
                title: 'Erro de Envio',
                message: `Falha no envio do e-mail:\n${error.message}`
            });
        } finally {
            setLoadingAction(null);
        }
    };

    const filteredAvisos = useMemo(() => {
        if (filterClienteId === 'all') return avisos;
        return avisos.filter(a => a.clienteId === filterClienteId);
    }, [avisos, filterClienteId]);

    return (
        <div className="pt-8 md:pt-12 px-4 md:px-8 pb-12 font-sans text-white bg-slate-900 min-h-screen">
            {/* CABEÇALHO COM BOTÕES DE AÇÃO */}
            <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
                <div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-cyan-400">Avisos & Notificações</h1>
                    <p className="text-slate-400 mt-2">
                        Cadastre alertas e configure o disparo automático de lembretes de e-mail por vencimento de serviços.
                    </p>
                </div>
                <div className="flex gap-3 shrink-0">
                    {isUserAdmin && (
                        <button
                            onClick={() => setIsLogsModalOpen(true)}
                            className="bg-slate-800 hover:bg-slate-700 text-cyan-400 hover:text-cyan-300 border border-slate-700 px-5 py-3 rounded-xl transition-all text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Logs de Envio
                        </button>
                    )}
                    {isUserAdmin && (
                        <button
                            onClick={() => { setIsSmtpModalOpen(true); setIsSmtpEditing(false); }}
                            className="bg-slate-800 hover:bg-slate-700 text-indigo-400 hover:text-indigo-300 border border-slate-700 px-5 py-3 rounded-xl transition-all text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Configurar E-mail
                        </button>
                    )}
                    {!isReadOnly && (
                        <button
                            onClick={() => {
                                setFormData({ assunto: '', descricao: '', clienteId: '', clienteNome: '', clienteEmail: '', dataAviso: '', recorrencia: 'unico' });
                                setStatusMsg({ type: '', msg: '' });
                                setIsFormModalOpen(true);
                            }}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white px-5 py-3 rounded-xl transition-all text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-cyan-950/20"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Novo Alerta
                        </button>
                    )}
                </div>
            </header>

            {/* BARRA DE FILTROS */}
            <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-500"></div>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Filtro de Visualização</span>
                </div>
                <div className="w-full sm:w-72">
                    <select
                        value={filterClienteId}
                        onChange={e => setFilterClienteId(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-cyan-500 transition-all text-xs font-medium"
                    >
                        <option value="all">Todos os Clientes</option>
                        {allClients.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.nomeFantasia || c.name} ({c.tipo === 'contrato' ? 'Contrato' : 'Avulso'})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* LISTAGEM DE AVISOS ATIVOS (LARGURA TOTAL) */}
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 shadow-xl min-h-[400px]">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-cyan-300 uppercase tracking-wider">
                        Compromissos e Avisos Ativos
                    </h2>
                    <span className="text-xs text-slate-500 font-bold uppercase">
                        Total: {filteredAvisos.length} avisos
                    </span>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
                    </div>
                ) : filteredAvisos.length === 0 ? (
                    <div className="text-center py-12 text-slate-500 italic">
                        Nenhum aviso encontrado para os critérios selecionados.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-700/60 text-slate-400 text-xs font-bold uppercase tracking-wider">
                                    <th className="py-4 px-4 font-semibold">Assunto</th>
                                    <th className="py-4 px-4 font-semibold">Cliente</th>
                                    <th className="py-4 px-4 font-semibold">Data Limite</th>
                                    <th className="py-4 px-4 font-semibold text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/70">
                                {filteredAvisos.map(aviso => {
                                    const daysLeft = getDaysRemaining(aviso.dataAviso);
                                    let badgeColor = 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400';
                                    let badgeText = `${daysLeft} dias restantes`;

                                    if (daysLeft === 0) {
                                        badgeColor = 'bg-red-600/20 border border-red-500/40 text-red-400 animate-pulse';
                                        badgeText = 'Vence hoje!';
                                    } else if (daysLeft < 0) {
                                        badgeColor = 'bg-slate-800 border border-slate-700 text-slate-500';
                                        badgeText = `Vencido há ${Math.abs(daysLeft)} dias`;
                                    } else if (daysLeft <= 7) {
                                        badgeColor = 'bg-red-500/10 border border-red-500/20 text-red-400';
                                    } else if (daysLeft <= 15) {
                                        badgeColor = 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400';
                                    }

                                    // Recurrence text
                                    let recText = 'Único';
                                    if (aviso.recorrencia === 'mensal') recText = 'Mensal 🔄';
                                    else if (aviso.recorrencia === 'anual') recText = 'Anual 🔄';

                                    return (
                                        <tr 
                                            key={aviso.id} 
                                            className="hover:bg-slate-800/30 transition-colors border-b border-slate-700/50"
                                        >
                                            <td className="py-4 px-4">
                                                <div className="font-bold text-white text-base">{aviso.assunto}</div>
                                                {aviso.descricao && (
                                                    <div className="text-xs text-slate-400 font-medium max-w-md truncate mt-0.5" title={aviso.descricao}>
                                                        {aviso.descricao}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="font-semibold text-slate-200">{aviso.clienteNome}</div>
                                                {aviso.clienteEmail && (
                                                    <div className="text-xs text-slate-500 lowercase font-mono">
                                                        {aviso.clienteEmail}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-slate-200 font-mono font-semibold">{aviso.dataAviso}</span>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${badgeColor}`}>
                                                        {badgeText}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                                    <span>Recorrência: <span className="text-slate-400">{recText}</span></span>
                                                    <span>•</span>
                                                    {aviso.emailsDisparados?.['7days'] ? (
                                                        <span className="text-emerald-500/80 flex items-center gap-0.5">
                                                            ✓ Notificado 7d
                                                        </span>
                                                    ) : (
                                                        <span className="text-yellow-500/80">
                                                            ⌛ Aguardando (7d)
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {!isReadOnly && (
                                                        <button
                                                            onClick={() => handleDispararTesteManual(aviso.id)}
                                                            disabled={loadingAction === `test-aviso-${aviso.id}`}
                                                            title="Forçar envio de e-mail agora"
                                                            className="p-2 text-slate-400 hover:text-yellow-400 disabled:opacity-40 transition-colors bg-slate-800/80 rounded-lg hover:bg-slate-800 flex items-center justify-center border border-slate-700/30 w-8 h-8"
                                                        >
                                                            {loadingAction === `test-aviso-${aviso.id}` ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                            ) : (
                                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L22 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    )}
                                                    {!isReadOnly && (
                                                        <button
                                                            onClick={() => handleEditAviso(aviso)}
                                                            title="Editar"
                                                            className="p-2 text-slate-400 hover:text-cyan-400 transition-colors bg-slate-800/80 rounded-lg hover:bg-slate-800 flex items-center justify-center border border-slate-700/30 w-8 h-8"
                                                        >
                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {!isReadOnly && (
                                                        <button
                                                            onClick={() => handleDeleteAviso(aviso.id)}
                                                            title="Deletar"
                                                            className="p-2 text-slate-400 hover:text-red-400 transition-colors bg-slate-800/80 rounded-lg hover:bg-slate-800 flex items-center justify-center border border-slate-700/30 w-8 h-8"
                                                        >
                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* MODAL 1: CADASTRO / EDIÇÃO DE ALERTA */}
            {isFormModalOpen && (
                <div className="fixed inset-0 bg-black/75 flex justify-center items-center z-[120] p-4 font-sans backdrop-blur-sm animate-fadeIn">
                    <div className="bg-slate-800 rounded-2xl border border-slate-700/80 w-full max-w-lg shadow-2xl flex flex-col relative max-h-[90vh]">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-t-2xl"></div>
                        
                        <header className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="font-bold text-xl text-cyan-300 uppercase tracking-widest">
                                {formData.id ? 'Editar Alerta' : 'Novo Alerta'}
                            </h3>
                            <button 
                                onClick={() => setIsFormModalOpen(false)} 
                                className="text-slate-400 hover:text-white transition-colors text-xl font-bold"
                            >
                                ✕
                            </button>
                        </header>

                        <form onSubmit={handleSaveAviso} className="p-6 overflow-y-auto space-y-4 custom-scrollbar">
                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                                    Assunto / Evento *
                                </label>
                                <input
                                    required
                                    disabled={isReadOnly}
                                    type="text"
                                    placeholder="Ex: Vencimento do Antivírus"
                                    value={formData.assunto}
                                    onChange={e => setFormData({ ...formData, assunto: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 transition-all font-medium placeholder:text-slate-600 text-sm"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                                    Selecione o Cliente *
                                </label>
                                <select
                                    required
                                    disabled={isReadOnly}
                                    value={formData.clienteId}
                                    onChange={e => {
                                        const c = allClients.find(client => client.id === e.target.value);
                                        setFormData({ 
                                            ...formData, 
                                            clienteId: e.target.value,
                                            clienteNome: c ? (c.nomeFantasia || c.name) : '',
                                            clienteEmail: c?.email || ''
                                        });
                                    }}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 transition-all text-sm"
                                >
                                    <option value="">-- Selecione o Cliente --</option>
                                    {allClients.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.nomeFantasia || c.name} ({c.tipo === 'contrato' ? 'Contrato' : 'Avulso'})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {formData.clienteId && (
                                <div className="animate-fadeIn">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                                        E-mail de Destino (Separe múltiplos e-mails por ponto e vírgula ";")
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="cliente@email.com; outro@email.com"
                                        value={formData.clienteEmail}
                                        onChange={e => setFormData({ ...formData, clienteEmail: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 transition-all text-sm font-medium"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                                    Data Limite / Vencimento *
                                </label>
                                <input
                                    required
                                    disabled={isReadOnly}
                                    type="date"
                                    value={formData.dataAviso}
                                    onChange={e => setFormData({ ...formData, dataAviso: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 transition-all text-sm font-medium"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                                    Recorrência do Alerta
                                </label>
                                <select
                                    disabled={isReadOnly}
                                    value={formData.recorrencia || 'unico'}
                                    onChange={e => setFormData({ ...formData, recorrencia: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 transition-all text-sm font-medium"
                                >
                                    <option value="unico">Único (Sem recorrência)</option>
                                    <option value="mensal">Mensal</option>
                                    <option value="anual">Anual</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">
                                    Descrição Detalhada
                                </label>
                                <textarea
                                    disabled={isReadOnly}
                                    placeholder="Informações adicionais do serviço..."
                                    rows="3"
                                    value={formData.descricao}
                                    onChange={e => setFormData({ ...formData, descricao: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 transition-all text-sm font-medium resize-none placeholder:text-slate-600"
                                />
                            </div>

                            {statusMsg.msg && (
                                <div className={`p-3 rounded-lg text-xs font-bold ${
                                    statusMsg.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
                                }`}>
                                    {statusMsg.msg}
                                </div>
                            )}

                            <footer className="pt-4 border-t border-slate-700/50 flex gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsFormModalOpen(false)}
                                    className="flex-1 bg-slate-750 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-xl transition-all text-xs tracking-wider uppercase border border-slate-700"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loadingAction === 'save-aviso'}
                                    className="flex-1 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white font-bold py-3 rounded-xl transition-all text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow-lg shadow-cyan-950/20"
                                >
                                    {loadingAction === 'save-aviso' ? 'Gravando...' : formData.id ? 'Atualizar' : 'Cadastrar'}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL 2: CONFIGURAÇÃO DE E-MAIL */}
            {isSmtpModalOpen && isUserAdmin && (
                <div className="fixed inset-0 bg-black/75 flex justify-center items-center z-[120] p-4 font-sans backdrop-blur-sm animate-fadeIn">
                    <div className="bg-slate-800 rounded-2xl border border-slate-700/80 w-full max-w-md shadow-2xl flex flex-col relative max-h-[90vh]">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-t-2xl"></div>
                        
                        <header className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="font-bold text-xl text-indigo-300 uppercase tracking-widest">
                                Servidor de E-mail
                            </h3>
                            <div className="flex items-center gap-3">
                                {!isSmtpEditing && (
                                    <button
                                        type="button"
                                        onClick={() => setIsSmtpEditing(true)}
                                        className="text-xs text-indigo-400 hover:text-indigo-300 font-bold uppercase tracking-wider bg-indigo-500/10 hover:bg-indigo-500/20 px-3 py-1.5 rounded-lg transition-all"
                                    >
                                        Editar
                                    </button>
                                )}
                                <button 
                                    onClick={() => { setIsSmtpModalOpen(false); setIsSmtpEditing(false); }} 
                                    className="text-slate-400 hover:text-white transition-colors text-xl font-bold"
                                >
                                    ✕
                                </button>
                            </div>
                        </header>

                        <form onSubmit={handleSaveSmtp} className="p-6 overflow-y-auto space-y-4 custom-scrollbar">
                            {/* Seletor de Provedor */}
                            <div>
                                <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Provedor de Envio</label>
                                <div className="flex bg-slate-900 p-1 rounded-lg border border-slate-700/50">
                                    <button
                                        type="button"
                                        disabled={!isSmtpEditing}
                                        onClick={() => setSmtpConfig({ ...smtpConfig, provider: 'smtp' })}
                                        className={`flex-1 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all ${smtpConfig.provider !== 'resend' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}
                                    >
                                        SMTP Tradicional
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!isSmtpEditing}
                                        onClick={() => setSmtpConfig({ ...smtpConfig, provider: 'resend' })}
                                        className={`flex-1 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all ${smtpConfig.provider === 'resend' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}
                                    >
                                        Resend API
                                    </button>
                                </div>
                            </div>

                            {smtpConfig.provider === 'resend' ? (
                                <>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Resend API Key</label>
                                        <input
                                            required
                                            disabled={!isSmtpEditing}
                                            type={isSmtpEditing ? "text" : "password"}
                                            placeholder="re_..."
                                            value={smtpConfig.resendApiKey || ''}
                                            onChange={e => setSmtpConfig({ ...smtpConfig, resendApiKey: e.target.value })}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 text-xs font-mono"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">E-mail Remetente (Verificado no Resend)</label>
                                        <input
                                            required
                                            disabled={!isSmtpEditing}
                                            type="text"
                                            placeholder="onboarding@resend.dev"
                                            value={smtpConfig.resendFrom || ''}
                                            onChange={e => setSmtpConfig({ ...smtpConfig, resendFrom: e.target.value })}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 text-xs"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="col-span-2">
                                            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Host SMTP</label>
                                            <input
                                                required
                                                disabled={!isSmtpEditing}
                                                type="text"
                                                placeholder="smtp.exemplo.com"
                                                value={smtpConfig.host || ''}
                                                onChange={e => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 text-xs"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Porta</label>
                                            <input
                                                required
                                                disabled={!isSmtpEditing}
                                                type="number"
                                                placeholder="465"
                                                value={smtpConfig.port || ''}
                                                onChange={e => setSmtpConfig({ ...smtpConfig, port: e.target.value })}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 text-xs"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">E-mail Remetente / Usuário</label>
                                        <input
                                            required
                                            disabled={!isSmtpEditing}
                                            type="email"
                                            placeholder="suporte@empresa.com"
                                            value={smtpConfig.user || ''}
                                            onChange={e => setSmtpConfig({ ...smtpConfig, user: e.target.value })}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 text-xs"
                                        />
                                    </div>

                                    {isSmtpEditing && (
                                        <div>
                                            <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Senha SMTP</label>
                                            <input
                                                required
                                                type="password"
                                                placeholder="••••••••••••"
                                                value={smtpConfig.pass || ''}
                                                onChange={e => setSmtpConfig({ ...smtpConfig, pass: e.target.value })}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Nome Remetente (Exibição)</label>
                                        <input
                                            required
                                            disabled={!isSmtpEditing}
                                            type="text"
                                            placeholder="Arcus Solutions &lt;suporte@arcus.com&gt;"
                                            value={smtpConfig.from || ''}
                                            onChange={e => setSmtpConfig({ ...smtpConfig, from: e.target.value })}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 text-xs"
                                        />
                                    </div>
                                </>
                            )}

                            {smtpStatusMsg.msg && (
                                <div className={`p-3 rounded-lg text-xs font-bold ${
                                    smtpStatusMsg.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
                                }`}>
                                    {smtpStatusMsg.msg}
                                </div>
                            )}

                            {!isSmtpEditing && (
                                <div className="animate-fadeIn">
                                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Destinatário do Teste (Separe por ";" para múltiplos)</label>
                                    <input
                                        type="text"
                                        placeholder="destinatario@empresa.com; outro@empresa.com"
                                        value={testRecipientEmail}
                                        onChange={e => setTestRecipientEmail(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:ring-1 focus:ring-indigo-500 text-xs mb-2 font-medium"
                                    />
                                </div>
                            )}

                            <footer className="pt-4 border-t border-slate-700/50 flex gap-3 mt-4">
                                {isSmtpEditing ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setIsSmtpEditing(false)}
                                            className="flex-1 bg-slate-750 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-xl transition-all text-xs uppercase border border-slate-700"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loadingAction === 'save-smtp'}
                                            className="flex-1 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold py-3 rounded-xl transition-all text-xs uppercase shadow-lg shadow-indigo-950/20"
                                        >
                                            {loadingAction === 'save-smtp' ? 'Salvando...' : 'Salvar'}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setIsSmtpModalOpen(false)}
                                            className="flex-1 bg-slate-750 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-xl transition-all text-xs uppercase border border-slate-700"
                                        >
                                            Fechar
                                        </button>
                                        <button
                                            type="button"
                                            disabled={loadingAction === 'test-smtp' || (smtpConfig.provider === 'resend' ? !smtpConfig.resendApiKey : !smtpConfig.host)}
                                            onClick={() => handleDispararTesteManual()}
                                            className="flex-1 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 text-white font-bold py-3 rounded-xl transition-all text-xs uppercase flex items-center justify-center gap-2 shadow-lg shadow-indigo-950/20"
                                        >
                                            {loadingAction === 'test-smtp' ? (
                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                            ) : 'Enviar Teste'}
                                        </button>
                                    </>
                                )}
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL DE FEEDBACK */}
            {feedbackModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[200] p-4 font-sans backdrop-blur-sm animate-fadeIn">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 text-center max-w-sm w-full relative shadow-2xl">
                        <div className={`absolute top-0 left-0 w-full h-1 rounded-t-xl ${feedbackModal.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                        
                        <div className="flex justify-center mb-4">
                            {feedbackModal.type === 'success' ? (
                                <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center text-emerald-400 text-xl font-bold">✓</div>
                            ) : (
                                <div className="w-12 h-12 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center text-red-400 text-xl font-bold">✕</div>
                            )}
                        </div>

                        <h3 className="text-xl font-bold text-white mb-2">{feedbackModal.title}</h3>
                        <p className="text-slate-300 text-xs mb-6 whitespace-pre-wrap leading-relaxed">{feedbackModal.message}</p>
                        
                        <button 
                            onClick={() => setFeedbackModal({ ...feedbackModal, isOpen: false })} 
                            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2.5 px-4 rounded-xl transition-all text-xs tracking-wider uppercase"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}

            {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO PERSONALIZADO */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 bg-black/75 flex justify-center items-center z-[200] p-4 font-sans backdrop-blur-sm animate-fadeIn">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 text-center max-w-sm w-full relative shadow-2xl">
                        <div className="absolute top-0 left-0 w-full h-1 rounded-t-xl bg-red-500"></div>
                        
                        <div className="flex justify-center mb-4">
                            <div className="w-12 h-12 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center text-red-400 text-2xl font-bold">
                                ⚠️
                            </div>
                        </div>

                        <h3 className="text-xl font-bold text-white mb-2">{confirmModal.title}</h3>
                        <p className="text-slate-300 text-xs mb-6 leading-relaxed">{confirmModal.message}</p>
                        
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })} 
                                className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 font-bold py-2.5 px-4 rounded-xl transition-all text-xs tracking-wider uppercase border border-slate-600"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmModal.onConfirm} 
                                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-2.5 px-4 rounded-xl transition-all text-xs tracking-wider uppercase shadow-lg shadow-red-950/20"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE HISTÓRICO DE LOGS */}
            {isLogsModalOpen && (
                <div className="fixed inset-0 bg-black/75 flex justify-center items-center z-[120] p-4 font-sans backdrop-blur-sm animate-fadeIn">
                    <div className="bg-slate-800 rounded-2xl border border-slate-700/80 w-full max-w-5xl shadow-2xl flex flex-col relative max-h-[90vh]">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-t-2xl"></div>
                        
                        <header className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-xl text-cyan-300 uppercase tracking-widest">
                                    Logs de Atividades
                                </h3>
                                <p className="text-xs text-slate-500 mt-1 font-medium">Histórico completo de cadastros, envios automáticos/manuais e alterações.</p>
                            </div>
                            <button 
                                onClick={() => setIsLogsModalOpen(false)} 
                                className="text-slate-400 hover:text-white transition-colors text-xl font-bold font-sans"
                            >
                                ✕
                            </button>
                        </header>

                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                            {logs.length === 0 ? (
                                <div className="text-center py-12 text-slate-500 italic">
                                    Nenhum log registrado ainda.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-700/60 text-slate-400 font-bold uppercase tracking-wider">
                                                <th className="py-3 px-3">Data/Hora</th>
                                                <th className="py-3 px-3">Ação</th>
                                                <th className="py-3 px-3">Usuário</th>
                                                <th className="py-3 px-3">Alerta / Cliente</th>
                                                <th className="py-3 px-3">Detalhes</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/70 text-slate-300">
                                            {logs.map(log => {
                                                const dateStr = log.timestamp?.toDate 
                                                    ? log.timestamp.toDate().toLocaleString('pt-BR') 
                                                    : log.timestamp?.seconds 
                                                        ? new Date(log.timestamp.seconds * 1000).toLocaleString('pt-BR')
                                                        : 'Processando...';

                                                let actionBadge = 'bg-slate-700 text-slate-300 border border-slate-600/30';
                                                let actionLabel = log.action || 'Desconhecido';

                                                switch(log.action) {
                                                    case 'criar':
                                                        actionBadge = 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
                                                        actionLabel = 'Cadastro';
                                                        break;
                                                    case 'editar':
                                                        actionBadge = 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20';
                                                        actionLabel = 'Edição';
                                                        break;
                                                    case 'excluir':
                                                        actionBadge = 'bg-red-500/10 text-red-400 border border-red-500/20';
                                                        actionLabel = 'Exclusão';
                                                        break;
                                                    case 'envio_manual':
                                                        actionBadge = 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20';
                                                        actionLabel = 'Envio Manual';
                                                        break;
                                                    case 'envio_automatico':
                                                        actionBadge = 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20';
                                                        actionLabel = 'Envio Automático';
                                                        break;
                                                    case 'recorrencia_virada':
                                                        actionBadge = 'bg-purple-500/10 text-purple-400 border border-purple-500/20';
                                                        actionLabel = 'Recorrência';
                                                        break;
                                                    case 'teste_smtp':
                                                        actionBadge = 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
                                                        actionLabel = 'Teste SMTP';
                                                        break;
                                                    default:
                                                        break;
                                                }

                                                return (
                                                    <tr key={log.id} className="hover:bg-slate-900/40 transition-colors">
                                                        <td className="py-3 px-3 font-mono text-[11px] text-slate-400 whitespace-nowrap">
                                                            {dateStr}
                                                        </td>
                                                        <td className="py-3 px-3 whitespace-nowrap">
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${actionBadge}`}>
                                                                {actionLabel}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-3 font-medium text-slate-400 max-w-[120px] truncate" title={log.user}>
                                                            {log.user}
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <div className="font-semibold text-white max-w-[160px] truncate" title={log.avisoAssunto}>
                                                                {log.avisoAssunto}
                                                            </div>
                                                            <div className="text-[10px] text-slate-500 max-w-[160px] truncate" title={log.clienteNome}>
                                                                {log.clienteNome}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-3 text-slate-400 max-w-sm truncate" title={log.detalhes}>
                                                            {log.detalhes}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        
                        <footer className="p-6 border-t border-slate-700/50 flex justify-end">
                            <button
                                onClick={() => setIsLogsModalOpen(false)}
                                className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2.5 px-6 rounded-xl transition-all text-xs tracking-wider uppercase"
                            >
                                Fechar
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
}
